import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, inject, Injectable, signal } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ClaimsMapService } from '@app/admin/components/claims-map/claims-map.service';
import {
  ChartDetection,
  CREATED_DETECTIONS_FILTER,
  CREATED_DETECTIONS_TRAILING_FILTER,
  getFlagRulesFilters,
  getFlagsFilter,
  Playbook,
  PlaybookDetectionCategories,
} from '@app/admin/components/smartclaim/smartclaim-types';
import { ToastService } from '@app/root/providers/toast-service';
import config from '@config';
import { OpenSearchRequestFilter } from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { PROJECTION_INDEXES } from '@libs/shared/infrastructure/projection_indexes';
import { PatientAssistanceRequestSystemModel } from '@services/models/patient-assistance-request-system.model';
import { PatientAssistanceRequestSearchService } from '@services/patient-assistance-request.search.service';
import { SettingsService } from '@services/settings.service';
import { getPreviousDateRange } from '@shared/components/chitin/molecules/daterange-picker/date-range-presets';
import { DateRangePreset } from '@shared/models/date-range-preset';
import { OpenSearchAggsValueResult } from '@shared/providers/opensearch-api';
import { OpenSearchClient } from '@shared/providers/opensearch-client';
import { cssToken } from '@shared/utils/css-token';
import { ChartConfiguration } from 'chart.js';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

export type AgregatesPanelInfo = {
  detections: number;
  pharmacies: number;
  count: number;
  flags: Array<{ key: string; value: number }>;
};

@Injectable({
  providedIn: 'root',
})
export class PlaybookService {
  private settingsService = inject(SettingsService);
  private httpClient = inject(HttpClient);
  private toastService = inject(ToastService);
  private claimsMapService = inject(ClaimsMapService);
  private ccrSearchService = inject(PatientAssistanceRequestSearchService);
  private readonly openSearchClient = inject(OpenSearchClient);

  detailViewSwitched = new EventEmitter<void>();
  shouldInvalidateMapSize = new EventEmitter<void>();
  detailViewType = signal<'brightWatch' | 'queryWorkbench'>('brightWatch');

  currentViewIndexOverride = signal<number | null>(null);
  flagsListFilterValues = signal<string[]>(['']);
  chartClicked = signal(new EventEmitter<{ flag: string; title: string; current: number }>());

  getPlaybooks() {
    return this.settingsService
      .get<Record<string, Playbook>>('flags', 'playbooks')
      .pipe(
        map(playbooksObject => (playbooksObject ? Object.entries(playbooksObject).map(([key, value]) => ({ ...value, id: key })) : [])),
      );
  }

  getDataForPlaybook(
    playbook: Playbook,
    detectionCategories: PlaybookDetectionCategories,
    filters: OpenSearchRequestFilter[],
  ): Observable<Playbook> {
    return forkJoin({
      detections:
        playbook.id in detectionCategories && Object.values(detectionCategories[playbook.id] ?? {}).length > 0
          ? forkJoin(
              Object.values(detectionCategories[playbook.id]).map(detectionCategory =>
                detectionCategory.calculateFromNcpdps
                  ? this.claimsMapService
                      .fetchByPharmacy([...filters, ...getFlagRulesFilters(detectionCategory.flagRules), CREATED_DETECTIONS_FILTER])
                      .pipe(
                        map(result => {
                          if (result instanceof HttpErrorResponse) return { ...detectionCategory, value: undefined };
                          return { ...detectionCategory, value: result.total.count };
                        }),
                      )
                  : this.ccrSearchService
                      .countData([...filters, ...getFlagRulesFilters(detectionCategory.flagRules), CREATED_DETECTIONS_FILTER])
                      .pipe(map(({ count }) => ({ ...detectionCategory, value: count }))),
              ),
            )
          : of([]),
      current: this.getAgregatesPanelInfo([...filters, getFlagsFilter(playbook.flags), CREATED_DETECTIONS_FILTER]),
      previous: this.getAgregatesPanelInfo([...filters, getFlagsFilter(playbook.flags), CREATED_DETECTIONS_TRAILING_FILTER]),
      playbook: of(playbook),
    }).pipe(
      map(({ current, previous, detections, playbook }) => ({
        ...playbook,
        stats: [
          {
            id: 'claims',
            title: 'Claims with Detections',
            value:
              current instanceof HttpErrorResponse || previous instanceof HttpErrorResponse
                ? null
                : {
                    current: current.count,
                    previous: previous.count,
                  },
            tooltip: 'CIEM detection event within the adjudication workflow',
          },
          {
            id: 'pharmacies',
            title: 'Detected Pharmacies',
            value:
              current instanceof HttpErrorResponse || previous instanceof HttpErrorResponse
                ? null
                : {
                    current: current.pharmacies,
                    previous: previous.pharmacies,
                  },
            tooltip: 'Pharmacy(ies) associated with a CIEM detection event within the adjudication workflow',
          },
        ],
        detections:
          detections instanceof HttpErrorResponse
            ? null
            : detections.filter(detection => !!detection.value).sort((d1, d2) => (d2.value ?? -1) - (d1.value ?? -1)),
      })),
    );
  }

  getAgregatesPanelAllPlaybooksInfo(
    createdFilter: AbstractControl,
    playbooks: Playbook[],
    baseFilters: OpenSearchRequestFilter[],
  ): Observable<Record<string, { currentValue: number; valueDelta?: number; previousPeriodLabel: string }>> {
    let previousPeriodLabel = 'Previous Period';
    const previousFilters = cloneDeep(baseFilters).filter(f => f.field !== 'flagsList');
    let previousDateRange: { gte: string; lte: string } | undefined = undefined;
    if (createdFilter?.value) {
      const createdFilterValue = createdFilter.value as Record<string, unknown>;
      if ('preset' in createdFilterValue) {
        const previousDateRangePreset = getPreviousDateRange(createdFilterValue.preset as DateRangePreset);
        if (previousDateRangePreset) {
          previousDateRange = {
            gte: previousDateRangePreset.from.toISOString(),
            lte: previousDateRangePreset.to.toISOString(),
          };
          previousPeriodLabel = previousDateRangePreset.presetDisplayValue ?? previousPeriodLabel;
        }
      }
    }
    for (const filter of previousFilters) {
      if (filter.field === 'created' && filter.value.created.gte && filter.value.created.lte) {
        if (previousDateRange) {
          filter.value = {
            created: previousDateRange,
          };
        } else {
          const dateRangeLength = moment(filter.value.created.lte).diff(filter.value.created.gte, 'days');
          filter.value = {
            created: {
              gte: moment(filter.value.created.gte).subtract(dateRangeLength, 'days').toISOString(),
              lte: moment(filter.value.created.gte).subtract(1, 'millisecond').toISOString(),
            },
          };
        }
      }
    }

    const returnObservables: Record<string, Observable<{ currentValue: number; valueDelta?: number; previousPeriodLabel: string }>> = {};
    for (const playbook of playbooks) {
      const currentFilters = cloneDeep(baseFilters)
        .filter(f => f.field !== 'flagsList')
        .map(filter => {
          if (filter.field.includes('[PLAYBOOK_ID]')) {
            return {
              ...filter,
              field: filter.field.replace('[PLAYBOOK_ID]', playbook.id),
              value: {
                [filter.field.replace('[PLAYBOOK_ID]', playbook.id)]: filter.value[filter.field],
              },
            };
          }
          return filter;
        });
      currentFilters.push({
        field: 'flagsList',
        value: playbook.flags,
        operator: 'in',
      });
      const deltaFilters = cloneDeep(previousFilters).map(filter => {
        if (filter.field.includes('[PLAYBOOK_ID]')) {
          return {
            ...filter,
            field: filter.field.replace('[PLAYBOOK_ID]', playbook.id),
            value: {
              [filter.field.replace('[PLAYBOOK_ID]', playbook.id)]: filter.value[filter.field],
            },
          };
        }
        return filter;
      });
      deltaFilters.push({
        field: 'flagsList',
        value: playbook.flags,
        operator: 'in',
      });

      returnObservables[playbook.id] = forkJoin({
        current: this.getAgregatesPanelInfo(currentFilters),
        previous: this.getAgregatesPanelInfo(deltaFilters),
      }).pipe(
        map(({ current, previous }) => {
          let currentValue = 0;
          let valueDelta: number | undefined = undefined;

          if (current instanceof HttpErrorResponse) {
            this.toastService.showError("Couldn't load data.");
          } else {
            currentValue = current.detections;
          }
          if (previous instanceof HttpErrorResponse) {
            this.toastService.showError("Couldn't load data.");
          } else {
            valueDelta = previous.detections === 0 ? undefined : (currentValue - previous.detections) / previous.detections;
          }

          return { currentValue, valueDelta, previousPeriodLabel };
        }),
      );
    }
    return forkJoin(returnObservables);
  }

  getAgregatesPanelInfo(filter: OpenSearchRequestFilter[]): Observable<HttpErrorResponse | AgregatesPanelInfo> {
    return this.httpClient
      .post<AgregatesPanelInfo>(`${config.aggregatesUrl}/claims-flags/stats`, {
        filter,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.toastService.showError("Couldn't load data.");
          return of(error);
        }),
      );
  }

  getAgregatesPanelCompareInfo(filter: OpenSearchRequestFilter[]): Observable<HttpErrorResponse | AgregatesPanelInfo> {
    return this.httpClient
      .post<AgregatesPanelInfo>(`${config.aggregatesUrl}/claims-flags/tenant-compare`, {
        filter,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.toastService.showError("Couldn't load data.");
          return of(error);
        }),
      );
  }

  getAgregatesPanelYtdInfo(filter: OpenSearchRequestFilter[]): Observable<HttpErrorResponse | AgregatesPanelInfo> {
    const ytdFilter = cloneDeep(filter);
    for (const f of ytdFilter) {
      if (f.field === 'created') {
        f.value = {
          created: {
            gte: moment().startOf('year').toISOString(),
            lte: moment().toISOString(),
          },
        };
      }
    }
    return this.getAgregatesPanelInfo(ytdFilter);
  }

  getSystemStatusForPlaybook(filter: OpenSearchRequestFilter[]): Observable<HttpErrorResponse | PatientAssistanceRequestSystemModel> {
    const loadFilter: OpenSearchRequestFilter[] = [
      ...filter,
      {
        field: 'created',
        operator: 'range',
        value: {
          created: {
            gte: moment().subtract(1, 'minute').toISOString(),
            lte: moment().toISOString(),
          },
        },
      },
    ];
    const load15Filter: OpenSearchRequestFilter[] = [
      ...filter,
      {
        field: 'created',
        operator: 'range',
        value: {
          created: {
            gte: moment().subtract(15, 'minutes').toISOString(),
            lte: moment().toISOString(),
          },
        },
      },
    ];
    return forkJoin({
      load: this.openSearchClient.aggs(PROJECTION_INDEXES.PATIENT_ASSISTANCE_REQUEST_GENERAL_VIEW, {
        filter: loadFilter,
        aggs: {
          systemLoad: {
            field: 'workflowPathCount',
            operator: 'sum',
          },
        },
      }),
      load15: this.openSearchClient.aggs(PROJECTION_INDEXES.PATIENT_ASSISTANCE_REQUEST_GENERAL_VIEW, {
        filter: load15Filter,
        aggs: {
          systemLoad15: {
            field: 'workflowPathCount',
            operator: 'sum',
          },
        },
      }),
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastService.showError('Failed to load system load info. Please contact customer support.');
        return of(error);
      }),
      map(results => {
        if (results instanceof HttpErrorResponse) {
          return results;
        }
        return {
          EPM: (results.load.systemLoad as OpenSearchAggsValueResult).value,
          systemLoad: (results.load15.systemLoad15 as OpenSearchAggsValueResult).value,
        };
      }),
    );
  }

  getChartData(
    playbook: Playbook,
    commonFilters: OpenSearchRequestFilter[],
    chartViewType: 'current' | 'trailing',
    flagKeyToTitleMap: Record<string, string>,
  ) {
    return forkJoin({
      chartData: this.getAgregatesPanelInfo([
        ...commonFilters,
        getFlagsFilter(playbook.flags),
        chartViewType === 'current' ? CREATED_DETECTIONS_FILTER : CREATED_DETECTIONS_TRAILING_FILTER,
      ]),
      flagsList: of(playbook.flags),
    }).pipe(
      map(({ chartData, flagsList }) =>
        chartData instanceof HttpErrorResponse
          ? null
          : this.getChartConfiguration(
              chartData.flags.map(flag => ({
                flag: flag.key,
                value: flag.value,
              })),
              flagsList,
              flagKeyToTitleMap,
            ),
      ),
    );
  }

  private getChartConfiguration(
    detections: ChartDetection[],
    flagsList: string[],
    flagKeyToTitleMap: Record<string, string>,
  ): ChartConfiguration | null {
    const jointDetections = flagsList
      .map(flag => ({
        flag,
        title: flag in flagKeyToTitleMap ? flagKeyToTitleMap[flag] : flag,
        current: detections.find(detection => detection.flag === flag)?.value ?? 0,
      }))
      .sort((a, b) => b.current - a.current);
    const currentHasData = jointDetections.findIndex(detection => detection.current > 0) !== -1;

    if (!currentHasData) return null;

    const data = {
      labels: jointDetections.map(detection => this.splitLongLabelForChart(detection.title)),
      datasets: currentHasData
        ? [
            {
              label: 'Current',
              data: jointDetections.map(detection => Math.log10(detection.current + 1.2)),
              fill: true,
              backgroundColor: cssToken('--color-chart-radar-blue-a20'),
              borderColor: cssToken('--color-chart-radar-blue'),
              pointBackgroundColor: cssToken('--color-chart-radar-blue'),
              pointBorderColor: cssToken('--color-divider'),
              pointHoverBackgroundColor: cssToken('--color-surface-subtle'),
              pointHoverBorderColor: cssToken('--color-chart-radar-blue'),
              pointHitRadius: 20,
            },
          ]
        : [],
    };

    return {
      type: 'radar',
      data,
      options: {
        onClick: (_event, activeElements) => {
          if (activeElements.length > 0) this.chartClicked().emit(jointDetections[activeElements[0].index]);
        },
        elements: {
          line: {
            borderWidth: 3,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        scales: {
          r: {
            ticks: {
              display: false,
            },
            pointLabels: {
              color: cssToken('--color-text'),
            },
            grid: {
              color: cssToken('--color-border'),
            },
            angleLines: {
              color: cssToken('--color-border'),
            },
          },
        },
        layout: {
          padding: {
            left: 70,
            right: 70,
            top: 0,
            bottom: 0,
          },
        },
        animation: {
          duration: 0,
        },
      },
      plugins: [
        {
          id: 'pingFirstPoint',
          afterDatasetsDraw(chart) {
            const ctx = chart.ctx;
            const meta = chart.getDatasetMeta(0);
            const point = meta.data[0];
            const pingColor = cssToken('--color-brand-pink-bright');

            const time = Date.now();
            const duration = 2000;
            const numRings = 2;

            for (let i = 0; i < numRings; i++) {
              const offset = i / numRings;
              const progress = ((time % duration) / duration + offset) % 1;

              const maxRadius = 20;
              const radius = progress * maxRadius;
              const opacity = (1 - progress) * 0.6;

              ctx.save();
              ctx.globalAlpha = opacity;
              ctx.beginPath();
              ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
              ctx.strokeStyle = pingColor;
              ctx.lineWidth = 2;
              ctx.stroke();
              ctx.restore();
            }

            ctx.save();
            ctx.beginPath();
            ctx.arc(point.x, point.y, 5, 0, Math.PI * 2);
            ctx.fillStyle = pingColor;
            ctx.fill();
            ctx.restore();

            requestAnimationFrame(() => chart.draw());
          },
        },
      ],
    };
  }

  private splitLongLabelForChart(label: string): string[] {
    const MAX_LINE_LENGTH = 35;
    if (label.length <= MAX_LINE_LENGTH) return [label];
    const splitLabel: string[] = [];

    let currentLine = '';
    const words = label.split(' ');
    for (const word of words) {
      if (currentLine.length + word.length > MAX_LINE_LENGTH) {
        splitLabel.push(currentLine);
        currentLine = `${word} `;
      } else {
        currentLine += `${word} `;
      }
    }

    return [...splitLabel, currentLine];
  }
}
